import { useEffect } from "react";
import { AlertTriangle, X, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/store/authStore";
import { useStopImpersonate } from "@/features/auth/useStopImpersonate";
import {
  IMPERSONATION_BAR_HEIGHT,
  IMPERSONATION_BAR_HEIGHT_VAR,
  useImpersonationVisible,
} from "./impersonation";

export function ImpersonationBar() {
  const user = useAuthStore((s) => s.user);
  const impersonation = useAuthStore((s) => s.impersonation);
  const { stop, isPending } = useStopImpersonate();
  const visible = useImpersonationVisible();

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(IMPERSONATION_BAR_HEIGHT_VAR, visible ? IMPERSONATION_BAR_HEIGHT : "0px");
    return () => {
      root.style.removeProperty(IMPERSONATION_BAR_HEIGHT_VAR);
    };
  }, [visible]);

  if (!visible) {
    return null;
  }

  return (
    // z-[60], not z-50: the sidebar is also fixed at z-50 and renders later in
    // the tree, so at equal z it painted over this bar and clipped the label to
    // "…nating:". A session-wide warning has to sit above the chrome it warns
    // about.
    <div
      className="fixed inset-x-0 top-0 z-[60] flex items-center bg-warning text-warning-foreground"
      style={{ height: IMPERSONATION_BAR_HEIGHT }}
      role="status"
    >
      {/* Full-bleed, not `container mx-auto`: a centred max-width box pulled the
          text inward on wide screens, straight under the sidebar.
          Below `md` the bar keeps to who is being viewed and the way out:
          the bar's height is fixed (the layout reserves exactly it), and at
          phone width the full line pushed the Stop button off the screen.
          Every `max-md:` class below is that, and nothing changes from `md`
          up. */}
      <div className="flex w-full items-center justify-between gap-4 px-4">
        <div className="flex items-center gap-3 max-md:min-w-0">
          <UserCog className="h-5 w-5 max-md:hidden" />
          <div className="flex items-center gap-2 max-md:min-w-0">
            <AlertTriangle className="h-4 w-4 max-md:shrink-0" />
            <span className="font-medium max-md:sr-only">Impersonating:</span>
            <span className="max-md:min-w-0 max-md:break-words">
              {user?.email}
            </span>
            {user?.companyName && (
              <span className="text-warning-foreground/80 max-md:hidden">
                ({user.companyName})
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 max-md:shrink-0">
          {impersonation.originalUser && (
            <span className="text-sm text-warning-foreground/80 max-md:hidden">
              Logged in as {impersonation.originalUser.email}
            </span>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void stop()}
            disabled={isPending}
          >
            <X className="h-4 w-4 mr-1" />
            {/* One run of text from `md` up, as before; below it the rest of
                the label is kept for screen readers only. */}
            <span>
              Stop <span className="max-md:sr-only">Impersonating</span>
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
}
